// FormazioneRisultatiScreen.tsx - Display voting results for a match

import React, {useState, useEffect, useMemo} from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  ActivityIndicator,
  Image,
  Dimensions,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {SafeAreaView} from 'react-native-safe-area-context';
import {RouteProp, useRoute, useNavigation} from '@react-navigation/native';
import {NativeStackNavigationProp} from '@react-navigation/native-stack';
import {useTheme} from '../hooks/useTheme';
import {Header} from '../components/Header';
import {Icon} from '../components/Icon';
import {API_CONFIG} from '../config/api';

const {width: SCREEN_WIDTH} = Dimensions.get('window');

// Session ID helper
const getSessionId = async (): Promise<string> => {
  const key = '@laviola_session_id';
  let sessionId = await AsyncStorage.getItem(key);
  if (!sessionId) {
    sessionId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
    await AsyncStorage.setItem(key, sessionId);
  }
  return sessionId;
};

// Types
interface Player {
  id: number;
  name: string;
  jersey_number: number;
  position: string;
  role: string;
  image: string | null;
}

interface SlotPlayer {
  player: Player;
  votes: number;
  percentage: number;
}

interface MatchInfo {
  match_id: string;
  home_team: {name: string; logo: string | null};
  away_team: {name: string; logo: string | null};
  match_date: string;
}

interface ResultsData {
  match: MatchInfo;
  total_votes: number;
  winning_formation: string | null;
  winning_formation_votes: number;
  formation_breakdown: {[key: string]: number};
  slots: {[key: string]: SlotPlayer[]};
  user_vote: string | null;
}

type RouteParams = {
  FormazioneRisultati: {matchId: string};
};

type RootStackParamList = {
  Home: undefined;
  FormazioneRisultati: {matchId: string};
};

// Formation row configs for different formations
const getFormationRows = (formation: string): number[][] => {
  // Parse formation like "3-4-3" or "4-3-1-2"
  const parts = formation.split('-').map(n => parseInt(n, 10));
  const rows: number[][] = [];
  
  // GK always first (slot 0)
  rows.push([0]);
  
  let slotIndex = 1;
  parts.forEach(count => {
    if (count > 0) {
      const row: number[] = [];
      for (let i = 0; i < count; i++) {
        row.push(slotIndex++);
      }
      rows.push(row);
    }
  });
  
  return rows;
};

// Determine role group for a slot based on position in formation
const getRoleGroup = (slotIndex: number, formation: string): string => {
  if (slotIndex === 0) return 'GK';
  
  const parts = formation.split('-').map(n => parseInt(n, 10));
  let current = 1;
  
  // First row after GK is DF
  if (slotIndex < current + parts[0]) return 'DF';
  current += parts[0];
  
  // Last row is FW
  const lastRowCount = parts[parts.length - 1];
  const totalSlots = parts.reduce((a, b) => a + b, 0);
  if (slotIndex > totalSlots - lastRowCount) return 'FW';
  
  // Everything else is MF
  return 'MF';
};

export const FormazioneRisultatiScreen: React.FC = () => {
  const {colors, isDark} = useTheme();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RouteParams, 'FormazioneRisultati'>>();
  const matchId = route.params?.matchId;
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ResultsData | null>(null);

  // Fetch results data
  useEffect(() => {
    const fetchData = async () => {
      if (!matchId) {
        setLoading(false);
        return;
      }
      try {
        const sessionId = await getSessionId();
        console.log('[FormazioneRisultati] Session ID:', sessionId);
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/app/formazione/results?match_id=${matchId}`, {
          headers: {
            'Content-Type': 'application/json',
            'X-Session-Id': sessionId,
          },
        });
        const json = await response.json();
        console.log('[FormazioneRisultati] API Response user_vote:', json.user_vote);
        if (json.success) {
          setData(json);
        }
      } catch (error) {
        console.error('Error fetching results:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [matchId]);

  // Calculate formation rows and winners
  const {formationRows, winners, benchPlayers} = useMemo(() => {
    if (!data || !data.winning_formation) {
      return {formationRows: [], winners: {}, benchPlayers: {}};
    }

    const formation = data.winning_formation;
    const rows = getFormationRows(formation);
    
    // Get winners (first/top player) for each slot
    const winnersMap: {[slotIndex: number]: SlotPlayer} = {};
    const benchMap: {[role: string]: SlotPlayer[]} = {
      GK: [],
      DF: [],
      MF: [],
      FW: [],
    };

    Object.entries(data.slots).forEach(([slotKey, players]) => {
      const match = slotKey.match(/slot-(\d+)/);
      if (!match) return;
      const slotIndex = parseInt(match[1], 10);
      
      if (players.length > 0) {
        // First player is the winner
        winnersMap[slotIndex] = players[0];
        
        // Rest go to bench grouped by role
        const roleGroup = getRoleGroup(slotIndex, formation);
        players.slice(1).forEach(benchPlayer => {
          if (benchPlayer.percentage > 0) {
            benchMap[roleGroup].push(benchPlayer);
          }
        });
      }
    });

    // Deduplicate bench by player id, keeping highest percentage
    const deduplicatedBench: {[role: string]: SlotPlayer[]} = {
      GK: [],
      DF: [],
      MF: [],
      FW: [],
    };

    Object.entries(benchMap).forEach(([role, players]) => {
      const playerMap: {[id: number]: SlotPlayer} = {};
      players.forEach(p => {
        if (!playerMap[p.player.id] || playerMap[p.player.id].percentage < p.percentage) {
          playerMap[p.player.id] = p;
        }
      });
      deduplicatedBench[role] = Object.values(playerMap).sort((a, b) => b.percentage - a.percentage);
    });

    return {
      formationRows: rows,
      winners: winnersMap,
      benchPlayers: deduplicatedBench,
    };
  }, [data]);

  // Render a player card
  const renderPlayerCard = (slotPlayer: SlotPlayer, index: number) => {
    const player = slotPlayer.player;
    const initial = player.name.split(' ').pop()?.charAt(0).toUpperCase() || '?';
    
    return (
      <View key={`player-${index}`} style={styles.playerCard}>
        <View style={styles.percentBadge}>
          <Text style={styles.percentText}>{slotPlayer.percentage}%</Text>
        </View>
        {player.image ? (
          <Image source={{uri: player.image}} style={styles.playerImage} />
        ) : (
          <View style={[styles.playerInitial, {backgroundColor: '#f0e8ff'}]}>
            <Text style={[styles.initialText, {color: '#8424e3'}]}>{initial}</Text>
          </View>
        )}
        <Text style={[styles.playerName, {color: colors.textPrimary}]} numberOfLines={2}>
          {player.name}
        </Text>
      </View>
    );
  };

  // Render formation row (players in that row of the formation)
  const renderFormationRow = (slotIndices: number[], rowIndex: number) => {
    const rowPlayers: SlotPlayer[] = [];
    slotIndices.forEach(slotIndex => {
      if (winners[slotIndex]) {
        rowPlayers.push(winners[slotIndex]);
      }
    });

    if (rowPlayers.length === 0) return null;

    return (
      <View key={`row-${rowIndex}`} style={styles.formationRow}>
        {rowPlayers.map((player, idx) => renderPlayerCard(player, rowIndex * 10 + idx))}
      </View>
    );
  };

  // Render bench section
  const renderBenchSection = () => {
    const roleLabels: {[key: string]: string} = {
      GK: 'Portieri',
      DF: 'Difensori',
      MF: 'Centrocampisti',
      FW: 'Attaccanti',
    };

    const roles = ['GK', 'DF', 'MF', 'FW'];
    const hasAnyBench = roles.some(role => benchPlayers[role]?.length > 0);
    
    if (!hasAnyBench) return null;

    return (
      <View style={[styles.benchCard, {backgroundColor: isDark ? colors.surface : '#fff'}]}>
        <Text style={[styles.sectionTitle, {color: colors.textPrimary}]}>Altri giocatori</Text>
        
        {roles.map(role => {
          const players = benchPlayers[role];
          if (!players || players.length === 0) return null;

          return (
            <View key={role} style={styles.benchGroup}>
              <Text style={styles.benchGroupLabel}>{roleLabels[role]}</Text>
              {players.map((player, idx) => (
                <View key={`bench-${role}-${idx}`}>
                  <View style={styles.benchRow}>
                    <Text style={[styles.benchPlayerName, {color: colors.textPrimary}]}>
                      {player.player.name}
                    </Text>
                    <Text style={styles.benchPlayerPercent}>{player.percentage}%</Text>
                  </View>
                  <View style={styles.benchProgressBar}>
                    <View style={[styles.benchProgressFill, {width: `${player.percentage}%`}]} />
                  </View>
                </View>
              ))}
            </View>
          );
        })}
      </View>
    );
  };

  if (loading) {
    return (
      <SafeAreaView style={[styles.container, {backgroundColor: colors.primary}]} edges={[]}>
        <Header 
          showActionBar 
          onBackPress={() => navigation.goBack()} 
          articleUrl="https://laviola.it/prossima-partita"
        />
        <View style={[styles.loadingContainer, {backgroundColor: colors.background}]}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </SafeAreaView>
    );
  }

  if (!data || data.total_votes === 0) {
    return (
      <SafeAreaView style={[styles.container, {backgroundColor: colors.primary}]} edges={[]}>
        <Header 
          showActionBar 
          onBackPress={() => navigation.goBack()} 
          articleUrl="https://laviola.it/prossima-partita"
        />
        <View style={[styles.emptyContainer, {backgroundColor: colors.background}]}>
          <Icon name="calendar-days" size={48} color={colors.textSecondary} />
          <Text style={[styles.emptyTitle, {color: colors.textPrimary}]}>
            Nessun Risultato Disponibile
          </Text>
          <Text style={[styles.emptyText, {color: colors.textSecondary}]}>
            Non ci sono ancora votazioni per questa partita.
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, {backgroundColor: colors.primary}]} edges={[]}>
      <Header 
        showActionBar 
        onBackPress={() => navigation.goBack()} 
        articleUrl="https://laviola.it/prossima-partita"
      />
      <ScrollView style={[styles.scrollView, {backgroundColor: colors.background}]} showsVerticalScrollIndicator={false}>
        {/* Match Header */}
        <View style={styles.matchHeader}>
          <View style={styles.teamRow}>
            {data.match.home_team.logo && (
              <Image source={{uri: data.match.home_team.logo}} style={styles.teamLogo} />
            )}
            <Text style={styles.teamName}>{data.match.home_team.name}</Text>
            <Text style={styles.vsText}>vs</Text>
            {data.match.away_team.logo && (
              <Image source={{uri: data.match.away_team.logo}} style={styles.teamLogo} />
            )}
            <Text style={styles.teamName}>{data.match.away_team.name}</Text>
          </View>
        </View>

        {/* User Vote Message */}
        {data.user_vote && (
          <View style={styles.userVoteBanner}>
            <Icon name="check" size={16} color="#22c55e" />
            <Text style={styles.userVoteText}>
              Hai già votato per il modulo <Text style={styles.userVoteFormation}>{data.user_vote}</Text>
            </Text>
          </View>
        )}

        {/* Winning Formation Section */}
        <View style={[styles.formationCard, {backgroundColor: isDark ? colors.surface : '#fff'}]}>
          <View style={styles.formationHeader}>
            <Text style={[styles.sectionTitle, {color: colors.textPrimary}]}>Formazione più votata</Text>
            <View style={styles.formationBadge}>
              <Text style={styles.formationText}>{data.winning_formation}</Text>
            </View>
          </View>

          {/* Formation Rows - each row represents a line in the formation */}
          <View style={styles.pitchContainer}>
            {formationRows.map((rowSlots, rowIndex) => renderFormationRow(rowSlots, rowIndex))}
          </View>
        </View>

        {/* Formation Stats */}
        <View style={[styles.statsCard, {backgroundColor: isDark ? colors.surface : '#fff'}]}>
          <Text style={[styles.sectionTitle, {color: colors.textPrimary}]}>Statistica Modulo</Text>
          
          {Object.entries(data.formation_breakdown)
            .sort(([, a], [, b]) => b - a)
            .map(([formation, votes]) => {
              const percentage = Math.round((votes / data.total_votes) * 100);
              const isWinner = formation === data.winning_formation;
              
              return (
                <View key={formation} style={styles.statRow}>
                  <View style={styles.statInfo}>
                    <Text style={[
                      styles.statFormation,
                      {color: isWinner ? '#8424e3' : colors.textPrimary},
                      isWinner && {fontWeight: '700'},
                    ]}>
                      {formation}
                    </Text>
                    <Text style={[
                      styles.statPercent,
                      {color: isWinner ? '#8424e3' : colors.textSecondary},
                    ]}>
                      {percentage}%
                    </Text>
                  </View>
                  <View style={styles.progressBar}>
                    <View
                      style={[
                        styles.progressFill,
                        {width: `${percentage}%`},
                      ]}
                    />
                  </View>
                </View>
              );
            })}
        </View>

        {/* Bench Section */}
        {renderBenchSection()}

        <View style={styles.bottomPadding} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginTop: 16,
  },
  emptyText: {
    fontSize: 14,
    textAlign: 'center',
    marginTop: 8,
  },
  matchHeader: {
    padding: 16,
    alignItems: 'center',
    backgroundColor: '#5d2d91',
  },
  teamRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  teamLogo: {
    width: 28,
    height: 28,
    marginHorizontal: 4,
  },
  teamName: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14,
    marginHorizontal: 4,
  },
  vsText: {
    color: 'rgba(255,255,255,0.7)',
    marginHorizontal: 8,
  },
  formationCard: {
    margin: 16,
    borderRadius: 12,
    padding: 16,
  },
  formationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
  },
  formationBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 16,
    backgroundColor: 'rgba(132, 36, 227, 0.1)',
    borderWidth: 1,
    borderColor: '#8424e3',
  },
  formationText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#8424e3',
  },
  pitchContainer: {
    alignItems: 'center',
  },
  formationRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginBottom: 16,
    width: '100%',
  },
  playerCard: {
    width: Math.min(72, (SCREEN_WIDTH - 64) / 5),
    alignItems: 'center',
    marginHorizontal: 4,
    marginBottom: 8,
    position: 'relative',
  },
  percentBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: '#8424e3',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 10,
    zIndex: 1,
  },
  percentText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '700',
  },
  playerImage: {
    width: 52,
    height: 52,
    borderRadius: 26,
    borderWidth: 2,
    borderColor: '#8424e3',
  },
  playerInitial: {
    width: 52,
    height: 52,
    borderRadius: 26,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#8424e3',
  },
  initialText: {
    fontSize: 20,
    fontWeight: '700',
  },
  playerName: {
    fontSize: 10,
    fontWeight: '600',
    marginTop: 4,
    textAlign: 'center',
  },
  statsCard: {
    marginHorizontal: 16,
    borderRadius: 12,
    padding: 16,
  },
  totalVotes: {
    fontSize: 12,
    marginTop: 4,
    marginBottom: 16,
  },
  statRow: {
    marginBottom: 12,
  },
  statInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  statFormation: {
    fontSize: 14,
    fontWeight: '500',
  },
  statPercent: {
    fontSize: 14,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: '#f3eefc',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#8424e3',
  },
  benchCard: {
    margin: 16,
    marginTop: 0,
    borderRadius: 12,
    padding: 16,
  },
  benchGroup: {
    marginTop: 16,
  },
  benchGroupLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: '#8424e3',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#f3eefc',
  },
  benchRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  benchPlayerName: {
    fontSize: 13,
  },
  benchPlayerPercent: {
    fontSize: 13,
    fontWeight: '600',
    color: '#8424e3',
  },
  benchProgressBar: {
    height: 6,
    borderRadius: 3,
    overflow: 'hidden',
    backgroundColor: '#f3eefc',
    marginBottom: 8,
  },
  benchProgressFill: {
    height: '100%',
    backgroundColor: '#cfc6ea',
  },
  bottomPadding: {
    height: 40,
  },
  userVoteBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0fdf4',
    paddingVertical: 10,
    paddingHorizontal: 16,
    marginHorizontal: 16,
    marginTop: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#bbf7d0',
  },
  userVoteText: {
    marginLeft: 8,
    fontSize: 13,
    color: '#166534',
  },
  userVoteFormation: {
    fontWeight: '700',
    color: '#15803d',
  },
});

export default FormazioneRisultatiScreen;
